import {
  createRouter,
  createRootRoute,
  createRoute,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { LoginPage, SignupPage } from './pages/LoginPage';
import { TermsPage } from './pages/TermsPage';
import { SitesGridPage } from './pages/SitesGridPage';

// SiteViewPage statically imports @wp-playground/client (~550 kB minified —
// ~40% of the bundle), so it's the one lazy route: login and the sites grid
// no longer download or parse the Playground runtime. User-approved
// exception to the no-dynamic-imports rule; SitesGridPage prefetches the
// chunk on mount so opening a site doesn't wait on it.
const SiteViewPage = lazyRouteComponent(
  () => import('./pages/SiteViewPage'),
  'SiteViewPage',
);
import { SettingsApiKeysPage } from './pages/settings/SettingsApiKeysPage';
import { DeviceAuthPage } from './pages/DeviceAuthPage';
import { HeroGalleryPage } from './pages/HeroGalleryPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';
import { SettingsAccountPage } from './pages/settings/SettingsAccountPage';
import { SettingsBillingPage } from './pages/settings/SettingsBillingPage';
import { SettingsPlansPage } from './pages/settings/SettingsPlansPage';
import { SettingsReferralPage } from './pages/settings/SettingsReferralPage';
import { SettingsAgentsPage } from './pages/settings/SettingsAgentsPage';
import { SettingsUsagePage } from './pages/settings/SettingsUsagePage';
import { SettingsTeamPage } from './pages/settings/SettingsTeamPage';
import { SettingsSitesPage } from './pages/settings/SettingsSitesPage';
import { TeamInvitePage } from './pages/TeamInvitePage';
import { SiteViewRedirectPage } from './pages/SiteViewRedirectPage';

// Root layout
const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Public route - login
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignupPage,
});

// Protected route - terms
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsPage,
});

// Protected route - sites grid (dashboard)
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: SitesGridPage,
});

// Protected route - individual site view
const siteViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sites/$siteId',
  component: SiteViewPage,
});

// Protected route - fresh view token, then back to the Playground view page.
// A root child, not nested under siteViewRoute, so it doesn't load the editor.
const siteViewRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sites/$siteId/view',
  component: SiteViewRedirectPage,
});

// Nested route - export dialog permalink (/sites/$siteId/export).
// This route exists only to make /export a matchable path so SiteViewPage
// (the parent) keeps rendering instead of falling through to not-found. It has
// no component output and SiteViewPage renders no <Outlet>, so nothing nested
// mounts. The modal's open state is derived in SiteViewPage from the URL path
// (see isExportOpen), not from this route rendering anything.
const siteExportRoute = createRoute({
  getParentRoute: () => siteViewRoute,
  path: 'export',
  component: () => null,
});

// Settings redirect - /settings -> /settings/account
const settingsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/account' });
  },
});

// Protected route - settings account
const settingsAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/account',
  component: SettingsAccountPage,
});

const settingsTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/team',
  component: SettingsTeamPage,
});

const settingsSitesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/sites',
  component: SettingsSitesPage,
});

// Protected route - settings plans
const settingsPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/plans',
  component: SettingsPlansPage,
});

// Protected route - settings usage
const settingsUsageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/usage',
  component: SettingsUsagePage,
});

// Protected route - settings billing
const settingsBillingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/billing',
  component: SettingsBillingPage,
});

// Protected route - API keys management
const apiKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/api-keys',
  component: SettingsApiKeysPage,
});

// Protected route - agents
const settingsAgentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/agents',
  component: SettingsAgentsPage,
});

// Protected route - referral partner dashboard
const settingsReferralRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/referrals',
  component: SettingsReferralPage,
});

// Protected route - device authorization approval
const deviceAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/device',
  component: DeviceAuthPage,
});

const teamInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/team/invite/$token',
  component: TeamInvitePage,
});

// Protected route - hero gallery
const heroGalleryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/heroes',
  component: HeroGalleryPage,
});

// Protected route - checkout success
const checkoutSuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/checkout/success',
  component: CheckoutSuccessPage,
});

// Redirect /plans → /settings/plans
const plansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plans',
  beforeLoad: () => {
    throw redirect({ to: '/settings/plans' });
  },
});

// Build the route tree
const routeTree = rootRoute.addChildren([
  loginRoute,
  signupRoute,
  termsRoute,
  indexRoute,
  siteViewRoute.addChildren([siteExportRoute]),
  siteViewRedirectRoute,
  settingsRedirectRoute,
  settingsAccountRoute,
  settingsTeamRoute,
  settingsSitesRoute,
  settingsPlansRoute,
  settingsUsageRoute,
  settingsBillingRoute,
  apiKeysRoute,
  settingsAgentsRoute,
  settingsReferralRoute,
  deviceAuthRoute,
  teamInviteRoute,
  heroGalleryRoute,
  checkoutSuccessRoute,
  plansRoute,
]);

// Create the router
export const router = createRouter({ routeTree });

// Type declaration for router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
